import { get, post, put, del, patch } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

// Mock data is only loaded in non-production builds. process.env.NODE_ENV is
// replaced with the literal string 'production' by webpack at build time,
// so the require() call below becomes unreachable dead code and mockData.js
// is excluded from the production bundle entirely.
const _mock = process.env.NODE_ENV !== 'production'
  // eslint-disable-next-line
  ? require('./mockData')
  : null;
const mockBookmarks    = _mock?.mockBookmarks    ?? [];
const mockTags         = _mock?.mockTags         ?? [];
const mockCollsData    = _mock?.mockCollections  ?? [];
const delay            = _mock?.delay            ?? (() => Promise.resolve());
const generateId       = _mock?.generateId       ?? (() => Math.random().toString(36).slice(2));

class BookmarkService {
  constructor() {
    this.apiName = 'bookmarker-api';
    this.mockMode = !process.env.REACT_APP_API_URL || process.env.REACT_APP_MOCK_MODE === 'true';
    this.mockBookmarks = [...mockBookmarks];
    this.mockTags = [...mockTags];
    this.mockCollections = [...mockCollsData];
  }

  // Get authentication headers
  async getAuthHeaders() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.accessToken.toString();
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw new Error('Authentication required');
    }
  }

  // Create a new bookmark
  async createBookmark(bookmarkData) {
    if (this.mockMode) {
      await delay();
      const newBookmark = {
        bookmarkId: generateId(),
        userId: 'mock-user',
        ...bookmarkData,
        status: 'processing',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      this.mockBookmarks.unshift(newBookmark);
      return newBookmark;
    }

    try {
      const headers = await this.getAuthHeaders();

      const response = await post({
        apiName: this.apiName,
        path: '/bookmarks',
        options: {
          headers,
          body: bookmarkData
        }
      }).response;

      return await response.body.json();
    } catch (error) {
      // DEBUG: log full error so we can diagnose false-positive duplicate detection
      console.warn('[createBookmark] error caught:', {
        statusCode: error?.response?.statusCode,
        body: error?.response?.body,
        message: error?.message,
        mockMode: this.mockMode,
      });
      if (error?.response?.statusCode === 409) {
        try {
          const body = JSON.parse(error.response.body);
          const dupError = new Error(body?.error?.message || 'This URL is already in your bookmarks');
          dupError.code = 'duplicate_url';
          dupError.existingId = body?.error?.existingId;
          throw dupError;
        } catch (parseErr) {
          if (parseErr.code === 'duplicate_url') throw parseErr;
          // JSON.parse failed — treat as 409 anyway so it shows as skipped, not failed
          const dupError = new Error('This URL is already in your bookmarks');
          dupError.code = 'duplicate_url';
          throw dupError;
        }
      }
      console.error('Error creating bookmark:', error);
      throw this.handleApiError(error);
    }
  }

  // Paginate through all bookmarks — used by export and full-library operations
  async getAllBookmarks() {
    if (this.mockMode) {
      await delay();
      return [...this.mockBookmarks];
    }
    const all = [];
    let nextToken = null;
    do {
      const response = await this.getBookmarks({ limit: 100, nextToken });
      all.push(...(response.items || []));
      nextToken = response.nextToken || null;
    } while (nextToken);
    return all;
  }

  // Get all bookmarks for the current user
  async getBookmarks(options = {}) {
    if (this.mockMode) {
      await delay();
      let items = [...this.mockBookmarks];
      
      if (options.status) {
        items = items.filter(b => b.status === options.status);
      }
      
      if (options.limit) {
        items = items.slice(0, options.limit);
      }
      
      return { items, nextToken: null };
    }

    try {
      const headers = await this.getAuthHeaders();
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.nextToken) queryParams.append('nextToken', options.nextToken);
      if (options.status) queryParams.append('status', options.status);
      
      const queryString = queryParams.toString();
      const path = queryString ? `/bookmarks?${queryString}` : '/bookmarks';

      const response = await get({
        apiName: this.apiName,
        path: path,
        options: { headers }
      }).response;
      
      const data = await response.body.json();
      
      // Return the full response object for pagination support
      if (data.items) {
        return {
          items: data.items,
          nextToken: data.nextToken
        };
      }
      
      // Handle legacy array response
      return {
        items: Array.isArray(data) ? data : [],
        nextToken: null
      };
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      throw this.handleApiError(error);
    }
  }

  // Get a specific bookmark
  async getBookmark(bookmarkId) {
    if (this.mockMode) {
      await delay();
      const bookmark = this.mockBookmarks.find(b => b.bookmarkId === bookmarkId);
      if (!bookmark) throw new Error('Bookmark not found');
      return bookmark;
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await get({
        apiName: this.apiName,
        path: `/bookmarks/${bookmarkId}`,
        options: { headers }
      }).response;

      return await response.body.json();
    } catch (error) {
      console.error('Error fetching bookmark:', error);
      throw this.handleApiError(error);
    }
  }

  // Update a bookmark
  async updateBookmark(bookmarkId, updateData) {
    if (this.mockMode) {
      await delay();
      const index = this.mockBookmarks.findIndex(b => b.bookmarkId === bookmarkId);
      if (index === -1) throw new Error('Bookmark not found');
      
      this.mockBookmarks[index] = {
        ...this.mockBookmarks[index],
        ...updateData,
        updatedAt: new Date().toISOString()
      };
      return this.mockBookmarks[index];
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await put({
        apiName: this.apiName,
        path: `/bookmarks/${bookmarkId}`,
        options: {
          headers,
          body: updateData
        }
      }).response;

      return await response.body.json();
    } catch (error) {
      console.error('Error updating bookmark:', error);
      throw this.handleApiError(error);
    }
  }

  // Delete a bookmark
  async deleteBookmark(bookmarkId) {
    if (this.mockMode) {
      await delay();
      const index = this.mockBookmarks.findIndex(b => b.bookmarkId === bookmarkId);
      if (index === -1) throw new Error('Bookmark not found');
      
      this.mockBookmarks.splice(index, 1);
      return { message: 'Bookmark deleted successfully' };
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const response = await del({
        apiName: this.apiName,
        path: `/bookmarks/${bookmarkId}`,
        options: { headers }
      }).response;

      return await response.body.json();
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      throw this.handleApiError(error);
    }
  }

  // Search bookmarks
  async searchBookmarks(query, options = {}) {
    if (this.mockMode) {
      await delay();
      const items = this.mockBookmarks.filter(bookmark => 
        bookmark.title?.toLowerCase().includes(query.toLowerCase()) ||
        bookmark.description?.toLowerCase().includes(query.toLowerCase()) ||
        bookmark.tags?.some(tag => tag.toLowerCase().includes(query.toLowerCase()))
      );
      return { items, count: items.length };
    }

    try {
      const headers = await this.getAuthHeaders();
      
      const queryParams = new URLSearchParams();
      queryParams.append('q', query);
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.tags && options.tags.length > 0) {
        queryParams.append('tags', options.tags.join(','));
      }
      
      const response = await get({
        apiName: this.apiName,
        path: `/bookmarks/search?${queryParams.toString()}`,
        options: { headers }
      }).response;

      const data = await response.body.json();
      
      // Return consistent format
      if (data.items) {
        return {
          items: data.items,
          count: data.count || data.items.length
        };
      }
      
      // Handle legacy array response
      const items = Array.isArray(data) ? data : [];
      return {
        items,
        count: items.length
      };
    } catch (error) {
      console.error('Error searching bookmarks:', error);
      throw this.handleApiError(error);
    }
  }

  // Get a page of tags. Returns { items, nextToken }.
  async getTags(options = {}) {
    if (this.mockMode) {
      await delay();
      const limit = options.limit || 100;
      const all = [...this.mockTags];
      const items = all.slice(0, limit);
      const nextToken = all.length > limit ? 'mock-next' : null;
      return { items, nextToken };
    }

    try {
      const headers = await this.getAuthHeaders();
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.nextToken) queryParams.append('nextToken', options.nextToken);
      const qs = queryParams.toString();

      const response = await get({
        apiName: this.apiName,
        path: `/tags${qs ? `?${qs}` : ''}`,
        options: { headers }
      }).response;

      const data = await response.body.json();
      return {
        items: data.items || [],
        nextToken: data.nextToken || null,
      };
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw this.handleApiError(error);
    }
  }

  // Delete a tag
  async deleteTag(tagName) {
    if (this.mockMode) {
      await delay();
      const index = this.mockTags.findIndex(t => t.tagName === tagName);
      if (index !== -1) this.mockTags.splice(index, 1);
      return;
    }

    try {
      const headers = await this.getAuthHeaders();
      await del({
        apiName: this.apiName,
        path: `/tags/${encodeURIComponent(tagName)}`,
        options: { headers }
      }).response;
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw this.handleApiError(error);
    }
  }

  // Get AI-suggested tag consolidation groups
  async consolidateTags() {
    if (this.mockMode) {
      await delay();
      return { suggestions: [] };
    }

    try {
      const headers = await this.getAuthHeaders();
      const response = await post({
        apiName: this.apiName,
        path: '/tags/consolidate',
        options: { headers, body: {} }
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error consolidating tags:', error);
      throw this.handleApiError(error);
    }
  }

  // Merge one tag into another across all bookmarks
  async mergeTags(from, into) {
    if (this.mockMode) {
      await delay();
      const fromIndex = this.mockTags.findIndex(t => t.tagName === from);
      if (fromIndex !== -1) this.mockTags.splice(fromIndex, 1);
      return { merged: 1, from, into };
    }

    try {
      const headers = await this.getAuthHeaders();
      const response = await post({
        apiName: this.apiName,
        path: '/tags/merge',
        options: { headers, body: { from, into } }
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error merging tags:', error);
      throw this.handleApiError(error);
    }
  }

  // Get admin configuration (admin only)
  async getAdminConfig() {
    if (this.mockMode) {
      await new Promise(r => setTimeout(r, 300));
      return { senderEmail: '', dailyReport: { enabled: false, reportEmail: '' } };
    }
    const headers = await this.getAuthHeaders();
    const response = await get({
      apiName: this.apiName,
      path: '/admin/config',
      options: { headers }
    }).response;
    return await response.body.json();
  }

  // Update admin configuration (admin only)
  async updateAdminConfig(config) {
    if (this.mockMode) {
      await new Promise(r => setTimeout(r, 300));
      return { success: true };
    }
    const headers = await this.getAuthHeaders();
    const response = await put({
      apiName: this.apiName,
      path: '/admin/config',
      options: { headers, body: config }
    }).response;
    return await response.body.json();
  }

  // Get usage stats for the current user
  async getUserStats() {
    if (this.mockMode) {
      await delay();
      return { bookmarkCount: 12, tagCount: 8, processedCount: 10, processingCount: 1, failedCount: 1, readLaterCount: 3 };
    }

    try {
      const headers = await this.getAuthHeaders();
      const response = await get({
        apiName: this.apiName,
        path: '/user/stats',
        options: { headers }
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw this.handleApiError(error);
    }
  }

  // Fetch a public profile by share token — unauthenticated, no Amplify needed
  async getPublicProfile(profileToken) {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      // Mock
      await delay();
      return {
        displayName: 'Demo User',
        bio: 'Avid bookmarker and web explorer.',
        avatarColor: '#4f46e5',
        publicCollections: [
          { collectionId: '1', name: 'AWS Resources', description: 'Cloud computing links', bookmarkCount: 4, shareToken: 'mock-token-1' },
          { collectionId: '2', name: 'Frontend Dev', description: 'React, CSS, and tools', bookmarkCount: 7, shareToken: 'mock-token-2' },
        ],
      };
    }
    const res = await fetch(`${apiUrl}/u/${profileToken}`);
    if (res.status === 404) throw new Error('Profile not found or not public');
    if (!res.ok) throw new Error('Failed to load profile');
    return res.json();
  }

  // Get user notification preferences
  async getUserPreferences() {
    if (this.mockMode) {
      await delay();
      return { digestEnabled: false, digestDay: 'MON', displayName: '', bio: '', avatarColor: '#4f46e5', profilePublic: false, profileShareToken: null };
    }
    try {
      const headers = await this.getAuthHeaders();
      const response = await get({
        apiName: this.apiName,
        path: '/user/preferences',
        options: { headers }
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      throw this.handleApiError(error);
    }
  }

  // Update user notification preferences
  async updateUserPreferences(preferences) {
    if (this.mockMode) {
      await delay();
      return preferences;
    }
    try {
      const headers = await this.getAuthHeaders();
      const response = await put({
        apiName: this.apiName,
        path: '/user/preferences',
        options: { headers, body: preferences }
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw this.handleApiError(error);
    }
  }

  // Delete all user data from DynamoDB (Cognito deletion handled client-side)
  async deleteUser() {
    if (this.mockMode) {
      await delay();
      return { deleted: true };
    }

    try {
      const headers = await this.getAuthHeaders();
      const response = await del({
        apiName: this.apiName,
        path: '/user',
        options: { headers }
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw this.handleApiError(error);
    }
  }

  // Get waitlist entries (admin only)
  async getWaitlist() {
    if (this.mockMode) {
      await new Promise(r => setTimeout(r, 300));
      return { items: [{ email: 'a@example.com', createdAt: new Date().toISOString(), status: 'pending' }], count: 1 };
    }
    const headers = await this.getAuthHeaders();
    const response = await get({
      apiName: this.apiName,
      path: '/admin/waitlist',
      options: { headers },
    }).response;
    return await response.body.json();
  }

  // Invite waitlist users by creating Cognito accounts and sending invite emails (admin only)
  async inviteUsers(emails) {
    if (this.mockMode) {
      await new Promise(r => setTimeout(r, 500));
      return { invited: emails, failed: [] };
    }
    const headers = await this.getAuthHeaders();
    const response = await post({
      apiName: this.apiName,
      path: '/admin/waitlist/invite',
      options: { headers, body: { emails } },
    }).response;
    return await response.body.json();
  }

  async listReinviteRequests() {
    if (this.mockMode) { await delay(); return { requests: [] }; }
    const headers = await this.getAuthHeaders();
    const response = await get({
      apiName: this.apiName,
      path: '/admin/reinvites',
      options: { headers },
    }).response;
    return await response.body.json();
  }

  async approveReinviteRequest(email) {
    if (this.mockMode) { await delay(); return { message: 'ok' }; }
    const headers = await this.getAuthHeaders();
    const response = await post({
      apiName: this.apiName,
      path: '/admin/reinvites/approve',
      options: { headers, body: { email } },
    }).response;
    return await response.body.json();
  }

  // Get feedback entries (admin only)
  async getFeedback({ nextToken } = {}) {
    if (this.mockMode) {
      await delay();
      return { items: [], nextToken: null, count: 0 };
    }
    const headers = await this.getAuthHeaders();
    const qs = nextToken ? `?nextToken=${encodeURIComponent(nextToken)}` : '';
    const response = await get({
      apiName: this.apiName,
      path: `/admin/feedback${qs}`,
      options: { headers }
    }).response;
    return await response.body.json();
  }

  async updateFeedback(feedbackId, userId, updates) {
    if (this.mockMode) { await delay(); return { feedbackId, ...updates }; }
    const headers = await this.getAuthHeaders();
    const response = await patch({
      apiName: this.apiName,
      path: `/admin/feedback/${encodeURIComponent(feedbackId)}`,
      options: { headers, body: { userId, ...updates } },
    }).response;
    return await response.body.json();
  }

  async deleteFeedback(feedbackId, userId) {
    if (this.mockMode) { await delay(); return { deleted: true }; }
    const headers = await this.getAuthHeaders();
    await del({
      apiName: this.apiName,
      path: `/admin/feedback/${encodeURIComponent(feedbackId)}?userId=${encodeURIComponent(userId)}`,
      options: { headers },
    }).response;
    return { deleted: true };
  }

  async getAdminUserStats() {
    if (this.mockMode) {
      await delay();
      return { users: [], summary: { totalUsers: 0, activeUsers7d: 0, activeUsers30d: 0, newUsers7d: 0, totalBookmarks: 0, processingIssues: 0, snapshotDate: null }, capturedAt: new Date().toISOString() };
    }
    const headers = await this.getAuthHeaders();
    const response = await get({ apiName: this.apiName, path: '/admin/users', options: { headers } }).response;
    return await response.body.json();
  }

  // Submit in-app feedback
  async submitFeedback({ note, page }) {
    if (this.mockMode) {
      await new Promise(r => setTimeout(r, 300));
      return { success: true };
    }
    const headers = await this.getAuthHeaders();
    const response = await post({
      apiName: this.apiName,
      path: '/feedback',
      options: { headers, body: { note, page } }
    }).response;
    return await response.body.json();
  }

  // Retry a failed bookmark
  async retryBookmark(bookmarkId) {
    if (this.mockMode) {
      await delay();
      const index = this.mockBookmarks.findIndex(b => b.bookmarkId === bookmarkId);
      if (index !== -1) this.mockBookmarks[index].status = 'processing';
      return this.mockBookmarks[index];
    }

    try {
      const headers = await this.getAuthHeaders();
      const response = await post({
        apiName: this.apiName,
        path: `/bookmarks/${bookmarkId}/retry`,
        options: { headers, body: {} }
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error retrying bookmark:', error);
      throw this.handleApiError(error);
    }
  }

  // ── Collections ──────────────────────────────────────────────────

  // List collections. Returns { items, hasMore }.
  async listCollections() {
    if (this.mockMode) {
      await delay();
      const items = this.mockCollections ? [...this.mockCollections] : [];
      return { items, hasMore: false };
    }
    try {
      const headers = await this.getAuthHeaders();
      const response = await get({
        apiName: this.apiName,
        path: '/collections',
        options: { headers },
      }).response;
      const data = await response.body.json();
      return {
        items: Array.isArray(data) ? data : data.items || [],
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error listing collections:', error);
      throw this.handleApiError(error);
    }
  }

  async createCollection(name, description = '') {
    if (this.mockMode) {
      await delay();
      if (!this.mockCollections) this.mockCollections = [];
      const c = { collectionId: generateId(), userId: 'mock-user', name, description, bookmarkIds: [], bookmarkCount: 0, createdAt: new Date().toISOString() };
      this.mockCollections.unshift(c);
      return c;
    }
    try {
      const headers = await this.getAuthHeaders();
      const { body } = await post({
        apiName: this.apiName,
        path: '/collections',
        options: { headers, body: { name, description } },
      }).response;
      return await body.json();
    } catch (error) {
      console.error('Error creating collection:', error);
      throw this.handleApiError(error);
    }
  }

  async getCollection(collectionId) {
    if (this.mockMode) {
      await delay();
      const c = (this.mockCollections || []).find(c => c.collectionId === collectionId);
      if (!c) throw new Error('Collection not found');
      const bookmarks = (c.bookmarkIds || []).map(id => this.mockBookmarks.find(b => b.bookmarkId === id)).filter(Boolean);
      return { ...c, bookmarks };
    }
    try {
      const headers = await this.getAuthHeaders();
      const response = await get({
        apiName: this.apiName,
        path: `/collections/${collectionId}`,
        options: { headers },
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error getting collection:', error);
      throw this.handleApiError(error);
    }
  }

  async updateCollection(collectionId, updates) {
    if (this.mockMode) {
      await delay();
      if (!this.mockCollections) return;
      const idx = this.mockCollections.findIndex(c => c.collectionId === collectionId);
      if (idx !== -1) this.mockCollections[idx] = { ...this.mockCollections[idx], ...updates };
      return this.mockCollections[idx];
    }
    try {
      const headers = await this.getAuthHeaders();
      const { body } = await put({
        apiName: this.apiName,
        path: `/collections/${collectionId}`,
        options: { headers, body: updates },
      }).response;
      return await body.json();
    } catch (error) {
      console.error('Error updating collection:', error);
      throw this.handleApiError(error);
    }
  }

  async deleteCollection(collectionId) {
    if (this.mockMode) {
      await delay();
      this.mockCollections = (this.mockCollections || []).filter(c => c.collectionId !== collectionId);
      return;
    }
    try {
      const headers = await this.getAuthHeaders();
      await del({
        apiName: this.apiName,
        path: `/collections/${collectionId}`,
        options: { headers },
      }).response;
    } catch (error) {
      console.error('Error deleting collection:', error);
      throw this.handleApiError(error);
    }
  }

  async addBookmarksToCollection(collectionId, bookmarkIds) {
    if (this.mockMode) {
      await delay();
      const c = (this.mockCollections || []).find(c => c.collectionId === collectionId);
      if (c) {
        bookmarkIds.forEach(id => { if (!c.bookmarkIds.includes(id)) { c.bookmarkIds.push(id); c.bookmarkCount++; } });
      }
      return c;
    }
    try {
      const headers = await this.getAuthHeaders();
      const { body } = await post({
        apiName: this.apiName,
        path: `/collections/${collectionId}/bookmarks`,
        options: { headers, body: { bookmarkIds } },
      }).response;
      return await body.json();
    } catch (error) {
      console.error('Error adding bookmarks to collection:', error);
      throw this.handleApiError(error);
    }
  }

  async shareCollection(collectionId) {
    if (this.mockMode) {
      await delay();
      if (!this.mockCollections) return;
      const token = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      const c = this.mockCollections.find(c => c.collectionId === collectionId);
      if (c) { c.shareToken = token; c.isShared = true; }
      return { shareToken: token, collection: c };
    }
    try {
      const headers = await this.getAuthHeaders();
      const { body } = await post({
        apiName: this.apiName,
        path: `/collections/${collectionId}/share`,
        options: { headers, body: {} },
      }).response;
      return await body.json();
    } catch (error) {
      console.error('Error sharing collection:', error);
      throw this.handleApiError(error);
    }
  }

  async unshareCollection(collectionId) {
    if (this.mockMode) {
      await delay();
      if (!this.mockCollections) return;
      const c = this.mockCollections.find(c => c.collectionId === collectionId);
      if (c) { delete c.shareToken; c.isShared = false; }
      return { success: true };
    }
    try {
      const headers = await this.getAuthHeaders();
      await del({
        apiName: this.apiName,
        path: `/collections/${collectionId}/share`,
        options: { headers },
      }).response;
      return { success: true };
    } catch (error) {
      console.error('Error unsharing collection:', error);
      throw this.handleApiError(error);
    }
  }

  async removeBookmarkFromCollection(collectionId, bookmarkId) {
    if (this.mockMode) {
      await delay();
      const c = (this.mockCollections || []).find(c => c.collectionId === collectionId);
      if (c) {
        c.bookmarkIds = c.bookmarkIds.filter(id => id !== bookmarkId);
        c.bookmarkCount = c.bookmarkIds.length;
      }
      return c;
    }
    try {
      const headers = await this.getAuthHeaders();
      await del({
        apiName: this.apiName,
        path: `/collections/${collectionId}/bookmarks/${bookmarkId}`,
        options: { headers },
      }).response;
    } catch (error) {
      console.error('Error removing bookmark from collection:', error);
      throw this.handleApiError(error);
    }
  }

  // ── Import ───────────────────────────────────────────────────────

  // Start a background import job. Returns { importId, total }.
  async startImport({ items, filename = '', reprocessDuplicates = false, createCollections = false }) {
    if (this.mockMode) {
      await delay();
      const importId = generateId();
      return { importId, total: items.length };
    }
    try {
      const headers = await this.getAuthHeaders();
      const response = await post({
        apiName: this.apiName,
        path: '/import',
        options: { headers, body: { items, filename, reprocessDuplicates, createCollections } },
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error starting import:', error);
      throw this.handleApiError(error);
    }
  }

  // Poll the status of a background import job.
  async getImportJob(importId) {
    if (this.mockMode) {
      await delay();
      // Simulate a completed job for mock mode
      return {
        importId,
        status: 'completed',
        total: 5,
        processed: 5,
        added: 4,
        skipped: 1,
        failed: 0,
        filename: 'mock-import.html',
        createdAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
      };
    }
    try {
      const headers = await this.getAuthHeaders();
      const response = await get({
        apiName: this.apiName,
        path: `/import/jobs/${importId}`,
        options: { headers },
      }).response;
      return await response.body.json();
    } catch (error) {
      console.error('Error getting import job:', error);
      throw this.handleApiError(error);
    }
  }

  // Handle API errors (Amplify v6 uses statusCode + body string, not status + data)
  handleApiError(error) {
    if (error?.response?.statusCode) {
      const status = error.response.statusCode;
      let msg = '';
      try {
        const parsed = JSON.parse(error.response.body);
        msg = parsed?.error?.message || parsed?.message || '';
      } catch {}
      switch (status) {
        case 400: return new Error(msg || 'Invalid request');
        case 401: return new Error('Authentication required');
        case 403: return new Error('Access denied');
        case 404: return new Error(msg || 'Not found');
        case 409: return new Error(msg || 'Conflict');
        case 429: return new Error('Too many requests. Please try again later.');
        case 500: return new Error(msg || 'Server error. Please try again later.');
        default:  return new Error(msg || `Request failed (${status})`);
      }
    }
    if (error?.message) return error;
    return new Error('Network error. Please check your connection.');
  }
}

// Create and export a singleton instance
export const bookmarkService = new BookmarkService();
